import { useMemo } from 'react'
import capitalize from 'lodash/capitalize'
import { dayjs } from '../../../utils/datetime/dayjs'
import { useMessages } from '../../../utils/intl/intl.hooks'
import { type DateRange } from '../DateRangePicker'

export enum DateRangePickerPresetOption {
  Yesterday = 'yesterday',
  Last7Days = 'last_7_days',
  Last28Days = 'last_28_days',
  Last84Days = 'last_84_days',
  LastWeek = 'last_week',
  TwoWeeksAgo = 'two_weeks_ago',
  ThreeWeeksAgo = 'three_weeks_ago',
  FourWeeksAgo = 'four_weeks_ago',
  FiveWeeksAgo = 'five_weeks_ago',
  Last30Days = 'last_30_days',
  LastMonth = 'last_month',
  Last90Days = 'last_90_days',
  QuarterToDate = 'quarter_to_date',
  Last120Days = 'last_120_days',
  LastYear = 'last_1_year',
  YearToDate = 'year_to_date',
  Last2Years = 'last_2_years',
  Tomorrow = 'tomorrow',
  Next7Days = 'next_7_days',
  NextWeek = 'next_week',
  NextMonth = 'next_month',
  Custom = 'custom',
}

export enum WeekStartDay {
  Saturday = 'saturday',
  Sunday = 'sunday',
  Monday = 'monday',
  Tuesday = 'tuesday',
  Wednesday = 'wednesday',
  Thursday = 'thursday',
  Friday = 'friday',
}

export enum ComparisonPeriod {
  LastYear = 'lastYear',
  LastPeriod = 'lastPeriod',
  LastYearDayOfWeekMatch = 'lastYearDayOfWeekMatch',
  LastPeriodDayOfWeekMatch = 'lastPeriodDayOfWeekMatch',
}

// Hook for localized comparison period labels - now as a hook with original name
export const useComparisonPeriodCopy = () =>
  useMessages({
    lastYear: 'sharedComponents.dateRangePicker.comparisonPeriod.lastYear',
    lastPeriod: 'sharedComponents.dateRangePicker.comparisonPeriod.lastPeriod',
    lastYearDayOfWeekMatch:
      'sharedComponents.dateRangePicker.comparisonPeriod.lastYearDayOfWeekMatch',
    lastPeriodDayOfWeekMatch:
      'sharedComponents.dateRangePicker.comparisonPeriod.lastPeriodDayOfWeekMatch',
  })

// Backward compatibility - keeping original hardcoded labels
export const comparisonPeriodCopy = {
  lastYear: 'Last Year',
  lastPeriod: 'Last Period',
  lastYearDayOfWeekMatch: 'Last Year - Day of week match',
  lastPeriodDayOfWeekMatch: 'Last Period - Day of week match',
}

// Backward compatibility - keeping original hardcoded labels
export const weekStartDayOptions = [
  {
    value: WeekStartDay.Saturday,
    label: capitalize(WeekStartDay.Saturday),
  },
  {
    value: WeekStartDay.Sunday,
    label: capitalize(WeekStartDay.Sunday),
  },
  {
    value: WeekStartDay.Monday,
    label: capitalize(WeekStartDay.Monday),
  },
  {
    value: WeekStartDay.Tuesday,
    label: capitalize(WeekStartDay.Tuesday),
  },
  {
    value: WeekStartDay.Wednesday,
    label: capitalize(WeekStartDay.Wednesday),
  },
  {
    value: WeekStartDay.Thursday,
    label: capitalize(WeekStartDay.Thursday),
  },
  {
    value: WeekStartDay.Friday,
    label: capitalize(WeekStartDay.Friday),
  },
]

// Hook for localized week start day options - now as a hook with original name
export const useWeekStartDayOptions = () => {
  const messages = useMessages({
    saturday: 'sharedComponents.dateRangePicker.weekStartDay.saturday',
    sunday: 'sharedComponents.dateRangePicker.weekStartDay.sunday',
    monday: 'sharedComponents.dateRangePicker.weekStartDay.monday',
    tuesday: 'sharedComponents.dateRangePicker.weekStartDay.tuesday',
    wednesday: 'sharedComponents.dateRangePicker.weekStartDay.wednesday',
    thursday: 'sharedComponents.dateRangePicker.weekStartDay.thursday',
    friday: 'sharedComponents.dateRangePicker.weekStartDay.friday',
  })

  return [
    {
      value: WeekStartDay.Saturday,
      label: messages.saturday,
    },
    {
      value: WeekStartDay.Sunday,
      label: messages.sunday,
    },
    {
      value: WeekStartDay.Monday,
      label: messages.monday,
    },
    {
      value: WeekStartDay.Tuesday,
      label: messages.tuesday,
    },
    {
      value: WeekStartDay.Wednesday,
      label: messages.wednesday,
    },
    {
      value: WeekStartDay.Thursday,
      label: messages.thursday,
    },
    {
      value: WeekStartDay.Friday,
      label: messages.friday,
    },
  ]
}

const weekStartDayToDateDifference = {
  [WeekStartDay.Saturday]: -1,
  [WeekStartDay.Sunday]: 0,
  [WeekStartDay.Monday]: 1,
  [WeekStartDay.Tuesday]: 2,
  [WeekStartDay.Wednesday]: 3,
  [WeekStartDay.Thursday]: 4,
  [WeekStartDay.Friday]: 5,
}

export interface DateRangePickerPresetConfig {
  start: Date
  end: Date
  key: DateRangePickerPresetOption
  label: string
}

// Backward compatibility - keeping original hardcoded labels
export const dateRangePickerPastDatesPresetLabels = {
  [DateRangePickerPresetOption.Yesterday]: 'Yesterday',
  [DateRangePickerPresetOption.Last7Days]: 'Last 7 Days',
  [DateRangePickerPresetOption.Last28Days]: 'Last 28 Days',
  [DateRangePickerPresetOption.Last84Days]: 'Last 84 Days',
  [DateRangePickerPresetOption.LastWeek]: 'Last Week',
  [DateRangePickerPresetOption.TwoWeeksAgo]: '2 Weeks Ago',
  [DateRangePickerPresetOption.ThreeWeeksAgo]: '3 Weeks Ago',
  [DateRangePickerPresetOption.FourWeeksAgo]: '4 Weeks Ago',
  [DateRangePickerPresetOption.FiveWeeksAgo]: '5 Weeks Ago',
  [DateRangePickerPresetOption.Last30Days]: 'Last 30 Days',
  [DateRangePickerPresetOption.LastMonth]: 'Last Month',
  [DateRangePickerPresetOption.Last90Days]: 'Last 90 Days',
  [DateRangePickerPresetOption.QuarterToDate]: 'Quarter to Date',
  [DateRangePickerPresetOption.Last120Days]: 'Last 120 Days',
  [DateRangePickerPresetOption.LastYear]: 'Last Year',
  [DateRangePickerPresetOption.Last2Years]: 'Last 2 Years',
  [DateRangePickerPresetOption.YearToDate]: 'Year to Date',
}

// Hook for localized past date preset labels using template variables
export const usePastDatesPresetLabels = () => {
  const messages = useMessages({
    yesterday: 'sharedComponents.dateRangePicker.presets.yesterday',
    lastXDays: 'sharedComponents.dateRangePicker.presets.lastXDays',
    lastWeek: 'sharedComponents.dateRangePicker.presets.lastWeek',
    weeksAgo: 'sharedComponents.dateRangePicker.presets.weeksAgo',
    lastMonth: 'sharedComponents.dateRangePicker.presets.lastMonth',
    quarterToDate: 'sharedComponents.dateRangePicker.presets.quarterToDate',
    lastYear: 'sharedComponents.dateRangePicker.presets.lastYear',
    lastXYears: 'sharedComponents.dateRangePicker.presets.lastXYears',
    yearToDate: 'sharedComponents.dateRangePicker.presets.yearToDate',
  })

  return {
    [DateRangePickerPresetOption.Yesterday]: messages.yesterday,
    [DateRangePickerPresetOption.Last7Days]: messages.lastXDays.replace('{count}', '7'),
    [DateRangePickerPresetOption.Last28Days]: messages.lastXDays.replace('{count}', '28'),
    [DateRangePickerPresetOption.Last84Days]: messages.lastXDays.replace('{count}', '84'),
    [DateRangePickerPresetOption.LastWeek]: messages.lastWeek,
    [DateRangePickerPresetOption.TwoWeeksAgo]: messages.weeksAgo.replace('{count}', '2'),
    [DateRangePickerPresetOption.ThreeWeeksAgo]: messages.weeksAgo.replace('{count}', '3'),
    [DateRangePickerPresetOption.FourWeeksAgo]: messages.weeksAgo.replace('{count}', '4'),
    [DateRangePickerPresetOption.FiveWeeksAgo]: messages.weeksAgo.replace('{count}', '5'),
    [DateRangePickerPresetOption.Last30Days]: messages.lastXDays.replace('{count}', '30'),
    [DateRangePickerPresetOption.LastMonth]: messages.lastMonth,
    [DateRangePickerPresetOption.Last90Days]: messages.lastXDays.replace('{count}', '90'),
    [DateRangePickerPresetOption.QuarterToDate]: messages.quarterToDate,
    [DateRangePickerPresetOption.Last120Days]: messages.lastXDays.replace('{count}', '120'),
    [DateRangePickerPresetOption.LastYear]: messages.lastYear,
    [DateRangePickerPresetOption.Last2Years]: messages.lastXYears.replace('{count}', '2'),
    [DateRangePickerPresetOption.YearToDate]: messages.yearToDate,
  }
}

// Backward compatibility - keeping original hardcoded labels
export const dateRangePickerFutureDatesPresetLabels = {
  [DateRangePickerPresetOption.Tomorrow]: 'Tomorrow',
  [DateRangePickerPresetOption.Next7Days]: 'Next 7 Days',
  [DateRangePickerPresetOption.NextWeek]: 'Next Week',
  [DateRangePickerPresetOption.NextMonth]: 'Next Month',
}

// Hook for localized future date preset labels using template variables
const useFutureDatesPresetLabels = () => {
  const messages = useMessages({
    tomorrow: 'sharedComponents.dateRangePicker.presets.tomorrow',
    nextXDays: 'sharedComponents.dateRangePicker.presets.nextXDays',
    nextWeek: 'sharedComponents.dateRangePicker.presets.nextWeek',
    nextMonth: 'sharedComponents.dateRangePicker.presets.nextMonth',
  })

  return {
    [DateRangePickerPresetOption.Tomorrow]: messages.tomorrow,
    [DateRangePickerPresetOption.Next7Days]: messages.nextXDays.replace('{count}', '7'),
    [DateRangePickerPresetOption.NextWeek]: messages.nextWeek,
    [DateRangePickerPresetOption.NextMonth]: messages.nextMonth,
  }
}

export const buildDateRangePresetConfig = (
  presets: DateRangePickerPresetOption[] | undefined,
  weekStartDay: WeekStartDay,
  includeFutureDates?: boolean,
  localizedPastLabels?: Partial<Record<DateRangePickerPresetOption, string>>,
  localizedFutureLabels?: Partial<Record<DateRangePickerPresetOption, string>>
): DateRangePickerPresetConfig[] => {
  const pastPresets = [
    {
      start: dayjs().subtract(1, 'days').toDate(),
      end: dayjs().subtract(1, 'days').toDate(),
      key: DateRangePickerPresetOption.Yesterday,
      label:
        localizedPastLabels?.[DateRangePickerPresetOption.Yesterday] ??
        dateRangePickerPastDatesPresetLabels[DateRangePickerPresetOption.Yesterday],
    },
    {
      start: dayjs().subtract(7, 'days').toDate(),
      end: dayjs().subtract(1, 'days').toDate(),
      key: DateRangePickerPresetOption.Last7Days,
      label:
        localizedPastLabels?.[DateRangePickerPresetOption.Last7Days] ??
        dateRangePickerPastDatesPresetLabels[DateRangePickerPresetOption.Last7Days],
    },
    {
      start: dayjs().subtract(28, 'days').toDate(),
      end: dayjs().subtract(1, 'days').toDate(),
      key: DateRangePickerPresetOption.Last28Days,
      label:
        localizedPastLabels?.[DateRangePickerPresetOption.Last28Days] ??
        dateRangePickerPastDatesPresetLabels[DateRangePickerPresetOption.Last28Days],
    },
    {
      start: dayjs().subtract(84, 'days').toDate(),
      end: dayjs().subtract(1, 'days').toDate(),
      key: DateRangePickerPresetOption.Last84Days,
      label:
        localizedPastLabels?.[DateRangePickerPresetOption.Last84Days] ??
        dateRangePickerPastDatesPresetLabels[DateRangePickerPresetOption.Last84Days],
    },
    {
      start: dayjs()
        .subtract(1, 'week')
        .startOf('week')
        .add(weekStartDayToDateDifference[weekStartDay], 'day')
        .toDate(),
      end: dayjs()
        .subtract(1, 'weeks')
        .endOf('week')
        .add(weekStartDayToDateDifference[weekStartDay], 'day')
        .toDate(),
      key: DateRangePickerPresetOption.LastWeek,
      label:
        localizedPastLabels?.[DateRangePickerPresetOption.LastWeek] ??
        dateRangePickerPastDatesPresetLabels[DateRangePickerPresetOption.LastWeek],
    },
    {
      start: dayjs()
        .subtract(2, 'weeks')
        .startOf('week')
        .add(weekStartDayToDateDifference[weekStartDay], 'day')
        .toDate(),
      end: dayjs()
        .subtract(2, 'weeks')
        .endOf('week')
        .add(weekStartDayToDateDifference[weekStartDay], 'day')
        .toDate(),
      key: DateRangePickerPresetOption.TwoWeeksAgo,
      label:
        localizedPastLabels?.[DateRangePickerPresetOption.TwoWeeksAgo] ??
        dateRangePickerPastDatesPresetLabels[DateRangePickerPresetOption.TwoWeeksAgo],
    },
    {
      key: DateRangePickerPresetOption.ThreeWeeksAgo,
      label:
        localizedPastLabels?.[DateRangePickerPresetOption.ThreeWeeksAgo] ??
        dateRangePickerPastDatesPresetLabels[DateRangePickerPresetOption.ThreeWeeksAgo],
      start: dayjs()
        .subtract(3, 'weeks')
        .startOf('week')
        .add(weekStartDayToDateDifference[weekStartDay], 'day')
        .toDate(),
      end: dayjs()
        .subtract(3, 'weeks')
        .endOf('week')
        .add(weekStartDayToDateDifference[weekStartDay], 'day')
        .toDate(),
    },
    {
      key: DateRangePickerPresetOption.FourWeeksAgo,
      label:
        localizedPastLabels?.[DateRangePickerPresetOption.FourWeeksAgo] ??
        dateRangePickerPastDatesPresetLabels[DateRangePickerPresetOption.FourWeeksAgo],
      end: dayjs()
        .subtract(4, 'weeks')
        .startOf('week')
        .add(weekStartDayToDateDifference[weekStartDay], 'day')
        .add(6, 'day')
        .toDate(),
      start: dayjs()
        .subtract(4, 'weeks')
        .startOf('week')
        .add(weekStartDayToDateDifference[weekStartDay], 'day')
        .toDate(),
    },
    {
      key: DateRangePickerPresetOption.FiveWeeksAgo,
      label:
        localizedPastLabels?.[DateRangePickerPresetOption.FiveWeeksAgo] ??
        dateRangePickerPastDatesPresetLabels[DateRangePickerPresetOption.FiveWeeksAgo],
      end: dayjs()
        .subtract(5, 'weeks')
        .startOf('week')
        .add(weekStartDayToDateDifference[weekStartDay], 'day')
        .add(6, 'day')
        .toDate(),
      start: dayjs()
        .subtract(5, 'weeks')
        .startOf('week')
        .add(weekStartDayToDateDifference[weekStartDay], 'day')
        .toDate(),
    },
    {
      key: DateRangePickerPresetOption.Last30Days,
      label:
        localizedPastLabels?.[DateRangePickerPresetOption.Last30Days] ??
        dateRangePickerPastDatesPresetLabels[DateRangePickerPresetOption.Last30Days],
      end: dayjs().subtract(1, 'days').endOf('day').toDate(),
      start: dayjs().subtract(30, 'd').toDate(),
    },

    {
      key: DateRangePickerPresetOption.LastMonth,
      label:
        localizedPastLabels?.[DateRangePickerPresetOption.LastMonth] ??
        dateRangePickerPastDatesPresetLabels[DateRangePickerPresetOption.LastMonth],
      end: dayjs().subtract(1, 'months').endOf('month').toDate(),
      start: dayjs().subtract(1, 'months').date(1).toDate(),
    },

    {
      key: DateRangePickerPresetOption.Last90Days,
      label:
        localizedPastLabels?.[DateRangePickerPresetOption.Last90Days] ??
        dateRangePickerPastDatesPresetLabels[DateRangePickerPresetOption.Last90Days],
      end: dayjs().subtract(1, 'days').endOf('day').toDate(),
      start: dayjs().subtract(90, 'd').toDate(),
    },

    {
      key: DateRangePickerPresetOption.QuarterToDate,
      label:
        localizedPastLabels?.[DateRangePickerPresetOption.QuarterToDate] ??
        dateRangePickerPastDatesPresetLabels[DateRangePickerPresetOption.QuarterToDate],
      end: dayjs().subtract(1, 'days').endOf('day').toDate(),
      start: dayjs().startOf('Q').toDate(),
    },

    {
      key: DateRangePickerPresetOption.Last120Days,
      label:
        localizedPastLabels?.[DateRangePickerPresetOption.Last120Days] ??
        dateRangePickerPastDatesPresetLabels[DateRangePickerPresetOption.Last120Days],
      end: dayjs().subtract(1, 'days').endOf('day').toDate(),
      start: dayjs().subtract(120, 'd').toDate(),
    },

    {
      key: DateRangePickerPresetOption.LastYear,
      label:
        localizedPastLabels?.[DateRangePickerPresetOption.LastYear] ??
        dateRangePickerPastDatesPresetLabels[DateRangePickerPresetOption.LastYear],
      end: dayjs().subtract(1, 'days').endOf('day').toDate(),
      start: dayjs().subtract(1, 'y').toDate(),
    },

    {
      key: DateRangePickerPresetOption.Last2Years,
      label:
        localizedPastLabels?.[DateRangePickerPresetOption.Last2Years] ??
        dateRangePickerPastDatesPresetLabels[DateRangePickerPresetOption.Last2Years],
      end: dayjs().subtract(1, 'days').endOf('day').toDate(),
      start: dayjs().subtract(2, 'y').toDate(),
    },

    {
      key: DateRangePickerPresetOption.YearToDate,
      label:
        localizedPastLabels?.[DateRangePickerPresetOption.YearToDate] ??
        dateRangePickerPastDatesPresetLabels[DateRangePickerPresetOption.YearToDate],
      end: dayjs().subtract(1, 'days').endOf('day').toDate(),
      start: dayjs().startOf('year').toDate(),
    },
  ]

  const futurePresets = [
    {
      start: dayjs().add(1, 'days').toDate(),
      end: dayjs().add(1, 'days').toDate(),
      key: DateRangePickerPresetOption.Tomorrow,
      label:
        localizedFutureLabels?.[DateRangePickerPresetOption.Tomorrow] ??
        dateRangePickerFutureDatesPresetLabels[DateRangePickerPresetOption.Tomorrow],
    },
    {
      start: dayjs().add(1, 'days').toDate(),
      end: dayjs().add(7, 'days').toDate(),
      key: DateRangePickerPresetOption.Next7Days,
      label:
        localizedFutureLabels?.[DateRangePickerPresetOption.Next7Days] ??
        dateRangePickerFutureDatesPresetLabels[DateRangePickerPresetOption.Next7Days],
    },
    {
      start: dayjs()
        .add(1, 'week')
        .startOf('week')
        .add(weekStartDayToDateDifference[weekStartDay], 'day')
        .toDate(),
      end: dayjs()
        .add(1, 'weeks')
        .endOf('week')
        .add(weekStartDayToDateDifference[weekStartDay], 'day')
        .toDate(),
      key: DateRangePickerPresetOption.NextWeek,
      label:
        localizedFutureLabels?.[DateRangePickerPresetOption.NextWeek] ??
        dateRangePickerFutureDatesPresetLabels[DateRangePickerPresetOption.NextWeek],
    },
    {
      start: dayjs().add(1, 'months').date(1).toDate(),
      end: dayjs().add(1, 'months').endOf('month').toDate(),
      key: DateRangePickerPresetOption.NextMonth,
      label:
        localizedFutureLabels?.[DateRangePickerPresetOption.NextMonth] ??
        dateRangePickerFutureDatesPresetLabels[DateRangePickerPresetOption.NextMonth],
    },
  ]

  const concatenatedPresets = pastPresets.concat(futurePresets)

  if (!presets) {
    if (includeFutureDates) {
      return concatenatedPresets
    }
    return pastPresets
  }

  return presets
    .map(presetKey => concatenatedPresets.find(({ key }) => key === presetKey))
    .filter(Boolean) as DateRangePickerPresetConfig[]
}

export const buildPresetDateRangeOptions = (
  presetsConfig: DateRangePickerPresetConfig[],
  customLabel?: string
) =>
  presetsConfig
    .map(({ label, key }) => ({
      label,
      value: key,
      disabled: false,
    }))
    .concat({
      label: customLabel || 'Custom',
      value: DateRangePickerPresetOption.Custom,
      disabled: true,
    })

export const findMatchingPresetConfig = (
  dateRange: DateRange,
  presetsConfig: DateRangePickerPresetConfig[]
) =>
  presetsConfig.find(
    config =>
      dayjs(config.start).isSame(dateRange.start, 'day') &&
      dayjs(config.end).isSame(dateRange.end, 'day')
  )

export const findMatchingPresetConfigKey = (
  dateRange: DateRange,
  presetsConfig: DateRangePickerPresetConfig[]
) => findMatchingPresetConfig(dateRange, presetsConfig)?.key || DateRangePickerPresetOption.Custom

// Convenience hooks for complete localized functionality

/**
 * Hook that provides localized preset configuration using original name
 * This is the main hook to use for localized date range presets
 */
export const useBuildDateRangePresetConfig = (
  presets: DateRangePickerPresetOption[] | undefined,
  weekStartDay: WeekStartDay,
  includeFutureDates?: boolean
) => {
  const pastLabels = usePastDatesPresetLabels()
  const futureLabels = useFutureDatesPresetLabels()

  return useMemo(
    () =>
      buildDateRangePresetConfig(
        presets,
        weekStartDay,
        includeFutureDates,
        pastLabels,
        futureLabels
      ),
    [presets, weekStartDay, includeFutureDates, pastLabels, futureLabels]
  )
}

/**
 * Hook that provides localized preset options for dropdowns using original name
 */
export const useBuildPresetDateRangeOptions = (presetsConfig: DateRangePickerPresetConfig[]) => {
  const messages = useMessages({
    custom: 'sharedComponents.dateRangePicker.presets.custom',
  })

  return useMemo(
    () => buildPresetDateRangeOptions(presetsConfig, messages.custom),
    [presetsConfig, messages.custom]
  )
}
